// src/api/auth.js
import axios from "axios";
import { API_BASE } from "../settings/master";

export const apiAuth = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 900000,
});

const storeSession = u => {
  const uid = u?.id || u?.user_id;
  if (uid) localStorage.setItem("userId", uid);
  if (u?.org_id) localStorage.setItem("orgId", u.org_id);
  if (u?.roles) localStorage.setItem("roles", JSON.stringify(u.roles));
};

const readJSON = k => { try { const s = localStorage.getItem(k); return s ? JSON.parse(s) : null } catch { return null } };
const writeJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v ?? {})) } catch { } };

let meMem = null;
let meInfoMem = null;
let membershipsMem = null;
const inflight = new Map();

const SKIP_401_PATHS = [
  "/renew",
  "/auth/refresh",
  "/auth/learner/refresh",
  "/login",
  "/auth/pubkey",
  "/first-login",
  "/reset-password",
  "/app-login",
  "/app-logout",
  "/app-forgot-password",
  "/app-reset-password",
];



const getPath = url => {
  try { return new URL(url, API_BASE).pathname; } catch { return String(url || ""); }
};

const shouldSkip401 = url => {
  const p = getPath(url);
  return SKIP_401_PATHS.some(base => p === base || p.startsWith(base + "/"));
};

const isAuthScreen = () => {
  try {
    const p = window.location.pathname || "";
    return (
      p === "/login" ||
      p.startsWith("/first-login") ||
      p === "/learn/login" ||
      p === "/learn" ||
      p.startsWith("/reset-password") ||
      p === "/forgot-password" ||
      p.startsWith("/learn/reset-password")
    );
  } catch { return false; }
};

export const isLearnerContext = () => {
  const p = window.location.pathname || "";
  return p.startsWith("/learn");
};

export const refreshCtx = { isRefreshing: false, failedQueue: [] };

export const processQueue = (error) => {
  refreshCtx.failedQueue.forEach(p => (error ? p.reject(error) : p.resolve()));
  refreshCtx.failedQueue = [];
};

apiAuth.interceptors.response.use(
  r => { harvestExpFromResponse(r); return r; },
  async err => {
    const orig = err.config;
    const status = err?.response?.status;
    const url = orig?.url;

    // ไม่ใช่ 401, หรือเป็น path ที่ไม่ต้อง refresh, หรืออยู่หน้า login อยู่แล้ว → reject ตรงๆ
    if (status !== 401 || orig._retry || shouldSkip401(url) || isAuthScreen()) {
      return Promise.reject(err);
    }

    // ถ้ากำลัง refresh อยู่ → queue request นี้ไว้รอ
    if (refreshCtx.isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshCtx.failedQueue.push({ resolve, reject });
      }).then(() => apiAuth(orig)).catch(e => Promise.reject(e));
    }

    orig._retry = true;
    refreshCtx.isRefreshing = true;

    try {
      const refreshUrl = isLearnerContext() ? "/auth/learner/refresh" : "/auth/refresh";
      const r = await apiAuth.post(refreshUrl);
      harvestExpFromResponse(r);
      processQueue(null);
      return apiAuth(orig);
    } catch (refreshErr) {
      processQueue(refreshErr);
      triggerSessionExpired();
      return Promise.reject(refreshErr);
    } finally {
      refreshCtx.isRefreshing = false;
    }
  }
);

const setExpMs = t => { if (t && Number.isFinite(t)) localStorage.setItem("sessionExp", String(t)); };
const parseExp = v => {
  if (v == null) return null;
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v;
  if (typeof v === "string") {
    if (/^\d+$/.test(v)) return parseInt(v, 10) < 1e12 ? parseInt(v, 10) * 1000 : parseInt(v, 10);
    const ts = Date.parse(v); if (!Number.isNaN(ts)) return ts;
  }
  return null;
};
const harvestExpFromResponse = r => {
  const h = (r && r.headers) || {};
  const cand = h["x-session-exp"] ?? h["x-token-exp"] ?? h["x-exp"] ?? h["x-jwt-exp"] ?? r?.data?.session_exp ?? r?.data?.token_exp;
  const t = parseExp(cand);
  if (t) setExpMs(t);
};

let renewTimer = null;
let renewing = false;
const renewSession = async () => {
  if (renewing) return;
  renewing = true;
  try {
    const refreshUrl = isLearnerContext() ? "/auth/learner/refresh" : "/auth/refresh";
    const r = await apiAuth.post(refreshUrl);
    harvestExpFromResponse(r);
  } catch { }
  renewing = false;
};
const startRenewScheduler = () => {
  if (renewTimer) return;
  renewTimer = setInterval(() => {
    const raw = localStorage.getItem("sessionExp");
    if (!raw) return;
    const exp = parseInt(raw, 10);
    if (!Number.isFinite(exp)) return;
    const now = Date.now();
    if (exp > now && exp - now <= 10 * 60 * 1000) renewSession();
  }, 60 * 1000);
};
startRenewScheduler();

let sessionPopupShown = false;
// const getRoles = () => { try { const r = localStorage.getItem("roles"); return r ? JSON.parse(r) : []; } catch { return []; } };
const pickLoginPath = () => "/login";
export const triggerSessionExpired = () => {
  if (sessionPopupShown) return;
  sessionPopupShown = true;
  try {
    const loginPath = pickLoginPath();
    window.dispatchEvent(new CustomEvent("app:session-expired", { detail: { loginPath } }));
  } catch { }
};



//------------------------------------------User

export const fetchMe = () => {
  if (meMem) return Promise.resolve({ data: meMem });
  const cached = readJSON("meCache");
  if (cached) { meMem = cached; return Promise.resolve({ data: cached }); }
  if (inflight.has("me")) return inflight.get("me");
  const p = apiAuth.get("/me").then(r => {
    storeSession(r.data);
    meMem = r.data;
    writeJSON("meCache", r.data);
    return { data: r.data };
  }).finally(() => inflight.delete("me"));
  inflight.set("me", p);
  return p;
};



export const fetchMeInfo = () => {
  if (meInfoMem) return Promise.resolve({ data: meInfoMem });
  const cached = readJSON("meInfoCache");
  if (cached) { meInfoMem = cached; return Promise.resolve({ data: cached }); }
  if (inflight.has("me-info")) return inflight.get("me-info");
  const p = apiAuth.get("/me-info").then(r => {
    storeSession(r.data);
    meInfoMem = r.data;
    writeJSON("meInfoCache", r.data);
    return { data: r.data };
  }).finally(() => inflight.delete("me-info"));
  inflight.set("me-info", p);
  return p;
};

/* ============================================
 * ⭐ เพิ่ม: ดึงรายการ org + roles ของผู้ใช้ทุกอัน
 * GET /me/memberships  -> { user_id, active_org_id, memberships:[{ organization_id, roles, ... }] }
 * ============================================ */
export const fetchMemberships = ({
  page = 1,
  per_page = 10,
  search = '',
} = {}) => {

  const queryKey = `me-memberships-${page}-${per_page}-${search}`

  // ✅ ถ้าเป็น page 1 และไม่มี search → ใช้ cache ได้
  if (page === 1 && !search && membershipsMem) {
    return Promise.resolve({ data: membershipsMem })
  }

  // ✅ inflight แยกตาม query
  if (inflight.has(queryKey)) {
    return inflight.get(queryKey)
  }

  const p = apiAuth.get("/me/memberships", {
    params: { page, per_page, search }
  }).then(r => {

    // cache เฉพาะ default page
    if (page === 1 && !search) {
      membershipsMem = r.data
      writeJSON("membershipsCache", r.data)
    }

    return { data: r.data }

  }).finally(() => inflight.delete(queryKey))

  inflight.set(queryKey, p)

  return p
}

// export const fetchMemberships = () => {
//   if (membershipsMem) return Promise.resolve({ data: membershipsMem });
//   const cached = readJSON("membershipsCache");
//   if (cached) { membershipsMem = cached; return Promise.resolve({ data: cached }); }
//   if (inflight.has("me-memberships")) return inflight.get("me-memberships");

//   const p = apiAuth.get("/me/memberships").then(r => {
//     membershipsMem = r.data;
//     writeJSON("membershipsCache", r.data);
//     storeSession({ data: r.data });
//     return { data: r.data };
//   }).finally(() => inflight.delete("me-memberships"));

//   inflight.set("me-memberships", p);
//   return p;
// };

// export const fetchMemberships = () =>
//   apiAuth.get("/me/memberships").then(r => {
//     storeSession(r.data);
//     return { data: r.data };
//   });


/* ============================================
 * ⭐ เพิ่ม: เปลี่ยน active org
 * POST /me/select-org { organization_id }
 * เซิร์ฟเวอร์จะ set-cookie โทเค็นใหม่ให้เรา
 * จากนั้นให้ invalidates cache แล้วดึง me/me-info ใหม่ตามต้องการ
 * ============================================ */
export const selectOrg = async (organization_id) => {
  const { data } = await apiAuth.post("/me/select-org", { organization_id });
  // data: { status:"ok", active_org_id, roles }

  // 1. Create the object you want to store
  if (data?.active_org_id) {
    localStorage.setItem('orgId', data.active_org_id)
  }
  if (data?.roles) {
    localStorage.setItem('roles', JSON.stringify(data.roles))
  }

  try {
    const cachedStr = localStorage.getItem('membershipsCache')
    const cached = cachedStr ? JSON.parse(cachedStr) : {}
    const next = { ...cached, active_org_id: data.active_org_id }
    localStorage.setItem('membershipsCache', JSON.stringify(next))
  } catch { }
  // ล้าง cache/memo เพื่อให้ refetch แล้วได้ข้อมูลตรงกับ org ใหม่
  // invalidateAuthCaches({ keepMemberships: true, newActiveOrgId: data.active_org_id });

  // ถ้าต้องการได้ข้อมูลล่าสุดทันที (optional):
  // await Promise.all([fetchMe(), fetchMeInfo()]);
  membershipsMem = null
  return { data };
};


/* ============================================
 * ⭐ เพิ่ม: เปลี่ยน active org
 * POST /me/select-org { organization_id }
 * เซิร์ฟเวอร์จะ set-cookie โทเค็นใหม่ให้เรา
 * จากนั้นให้ invalidates cache แล้วดึง me/me-info ใหม่ตามต้องการ
 * ============================================ */
export const changeOrg = async (organization_id) => {
  const { data } = await apiAuth.post("/me/change-org", { organization_id });
  // data: { status:"ok", active_org_id, roles }

  // 1. Create the object you want to store
  if (data?.active_org_id) {
    localStorage.setItem('orgId', data.active_org_id)
  }
  if (data?.roles) {
    localStorage.setItem('roles', JSON.stringify(data.roles))
  }

  try {
    const cachedStr = localStorage.getItem('membershipsCaches')
    const cached = cachedStr ? JSON.parse(cachedStr) : {}
    const next = { ...cached, active_org_id: data.active_org_id }
    localStorage.setItem('membershipsCaches', JSON.stringify(next))
  } catch { }
  // ล้าง cache/memo เพื่อให้ refetch แล้วได้ข้อมูลตรงกับ org ใหม่
  // invalidateAuthCaches({ keepMemberships: true, newActiveOrgId: data.active_org_id });

  // ถ้าต้องการได้ข้อมูลล่าสุดทันที (optional):
  // await Promise.all([fetchMe(), fetchMeInfo()]);
  membershipsMem = null
  return { data };
};



/* ============================================
 * ⭐ Helper: ล้าง cache ทั้งหมดที่เกี่ยวกับ session
 *   - ใช้ตอน logout หรือหลัง selectOrg สำเร็จ
 *   - keepMemberships: true จะคงรายการสมาชิกภาพไว้ (UI ยังแสดง list ได้)
 * ============================================ */
export const invalidateAuthCaches = ({ keepMemberships = false, newActiveOrgId = null } = {}) => {
  // mem
  meMem = null;
  meInfoMem = null;

  // storage
  removeJSON("meCache");
  removeJSON("meInfoCache");

  // ถ้าจะเก็บ memberships ต่อก็อย่าลบ
  if (!keepMemberships) {
    membershipsMem = null;
    removeJSON("membershipsCache");
  } else if (membershipsMem && newActiveOrgId) {
    // sync active_org_id ในแคช memberships ให้ตรง (ช่วยให้ UI อัปเดตเร็ว)
    membershipsMem = { ...membershipsMem, active_org_id: newActiveOrgId };
    writeJSON("membershipsCache", membershipsMem);
  }

  // inflight keys (กันห้อย)
  ["me", "me-info", "me-memberships"].forEach(k => inflight.delete(k));
};

/* ============================================
 * (ออปชัน) Helper บางตัวที่ UI อาจอยากใช้
 * ============================================ */
export const getActiveOrgIdFromCache = () => {
  const me = meMem || readJSON("meCache");
  if (me?.active_org_id) return me.active_org_id;

  const memberships = membershipsMem || readJSON("membershipsCache");
  return memberships?.active_org_id || null;
};


export async function login(username, password) {
  try {
    const k = await getLoginPubkey();
    const kid = k?.data?.kid;
    const jwk = k?.data?.jwk;
    if (!kid || !jwk) throw new Error("no_key");
    const enc_password = await rsaEncrypt(password, jwk);
    const r = await apiAuth.post("/login", { username, enc_password, kid });
    harvestExpFromResponse(r);
    const data = r.data;
    storeSession(data.user);
    meMem = null;
    meInfoMem = null;
    localStorage.removeItem("meCache");
    localStorage.removeItem("meInfoCache");
    try { sessionPopupShown = false; } catch { }
    startRenewScheduler();
    return data.user;
  } catch {
    const r = await apiAuth.post("/login", { username, password });
    harvestExpFromResponse(r);
    const data = r.data;
    storeSession(data.user);
    meMem = null;
    meInfoMem = null;
    localStorage.removeItem("meCache");
    localStorage.removeItem("meInfoCache");
    try { sessionPopupShown = false; } catch { }
    startRenewScheduler();
    return data.user;
  }
}

export const register = async (username, email, password, extra = {}) => {
  try {
    const k = await getLoginPubkey()
    const kid = k?.data?.kid
    const jwk = k?.data?.jwk
    if (!kid || !jwk) throw new Error('no_key')
    const enc_password = await rsaEncrypt(password, jwk)
    return apiAuth.post('/register', { username, email, enc_password, kid, ...extra })
  } catch {
    return apiAuth.post('/register', { username, email, password, ...extra })
  }
}

export const confirmRegister = (token) =>
  apiAuth.post('/confirm-register', { token });




export async function logout() {
  try {
    await apiAuth.post("/logout");
  } catch { }
  meMem = null;
  meInfoMem = null;
  localStorage.clear();
  try { sessionPopupShown = false; } catch { }
  window.location.href = "/login";
}

export const getInviteInfo = (token, jwtToken) =>
  apiAuth.get(`/first-login/${encodeURIComponent(token)}?d=${jwtToken}`);



export const activateInvite = async (token, payload) => {
  try {
    if (payload?.password) {
      const k = await getLoginPubkey();
      const kid = k?.data?.kid;
      const jwk = k?.data?.jwk;
      if (kid && jwk) {
        const enc_password = await rsaEncrypt(payload.password, jwk);
        const body = { ...payload, enc_password, kid };
        delete body.password;
        return apiAuth.post(`/first-login/${encodeURIComponent(token)}`, body);
      }
    }
    return apiAuth.post(`/first-login/${encodeURIComponent(token)}`, payload);
  } catch {
    return apiAuth.post(`/first-login/${encodeURIComponent(token)}`, payload);
  }
};

export const acceptInvite = async (token, payload) => {
  try {

    if (payload?.accepted_at) {
      const body = { ...payload };

      return apiAuth.post(`/accept-invite/${encodeURIComponent(token)}`, body);
    }
  } catch {
    return apiAuth.post(`/accept-invite/${encodeURIComponent(token)}`, payload);
  }
}

export const socialLogin = provider =>
  (window.location.href = `${API_BASE}/oauth/${provider}`);

export const checkUsername = (username) =>
  apiAuth.get(`/check-username/${encodeURIComponent(username)}`).then(r => {
    return r;
  });

export const resetPassword = async (token, password) => {
  try {
    const k = await getLoginPubkey();
    const kid = k?.data?.kid;
    const jwk = k?.data?.jwk;
    if (!kid || !jwk) throw new Error("no_key");
    const enc_password = await rsaEncrypt(password, jwk);
    return apiAuth.post(`/reset-password/${encodeURIComponent(token)}`, { enc_password, kid });
  } catch {
    return apiAuth.post(`/reset-password/${encodeURIComponent(token)}`, { password });
  }
};

//------------------------------------------Learner

export const fetchLearnerMe = () => apiAuth.get("/app-me");

export const getLoginPubkey = () => apiAuth.get("/auth/pubkey");

const importRsaJwk = async (jwk) => {
  return await window.crypto.subtle.importKey(
    "jwk",
    jwk,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
};
const b64u = (buf) => {
  let s = btoa(String.fromCharCode(...new Uint8Array(buf)));
  return s.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};
const rsaEncrypt = async (plain, jwk) => {
  const key = await importRsaJwk(jwk);
  const enc = new TextEncoder().encode(plain);
  const ct = await window.crypto.subtle.encrypt({ name: "RSA-OAEP" }, key, enc);
  return b64u(ct);
};

export const loginAppLearner = async (username, password) => {
  try {
    const k = await getLoginPubkey();
    const kid = k?.data?.kid;
    const jwk = k?.data?.jwk;
    if (!kid || !jwk) throw new Error("no_key");
    const enc_password = await rsaEncrypt(password, jwk);
    return apiAuth.post("/app-login", { username, enc_password, kid });
  } catch {
    return apiAuth.post("/app-login", { username, password });
  }
};

export const forgotPasswordLearner = (identifier) =>
  apiAuth.post("/app-forgot-password", { query: identifier });

export const resetLearnerPassword = async (token, password) => {
  try {
    const k = await getLoginPubkey();
    const kid = k?.data?.kid;
    const jwk = k?.data?.jwk;
    if (!kid || !jwk) throw new Error("no_key");
    const enc_password = await rsaEncrypt(password, jwk);
    return apiAuth.post(`/app-reset-password/${encodeURIComponent(token)}`, { enc_password, kid });
  } catch {
    return apiAuth.post(`/app-reset-password/${encodeURIComponent(token)}`, { password });
  }
};

export const checkHasLearner = () => apiAuth.get("/has-learner");

export const myLearner = (org_id) => apiAuth.get("/me-role/learner?org_id=" + encodeURIComponent(org_id));
export const myAdmin = (org_id) => apiAuth.get("/me-role/admin?org_id=" + encodeURIComponent(org_id));

export const learnerMemberships = () => apiAuth.get("/learner-memberships");

export const learnerToken = (org_id) => apiAuth.post("/learner-token", { org_id });
export const adminToken = (org_id) => apiAuth.post("/admin-token", { org_id });

export const getSocialGoogle = () => apiAuth.get(`/social/google`);
export const authGoogle = (token) => apiAuth.post(`/auth/google`, { token }, { withCredentials: true });
export const authGoogleProfile = (token) => apiAuth.post(`/auth/google/profile`, { token }, { withCredentials: true });
export const authGoogleRegister = (token, payload = {}) => apiAuth.post(`/auth/google/register`, { token, ...payload }, { withCredentials: true });
export const disconnectGoogle = () => apiAuth.delete(`/google/disconnect`);
export default apiAuth;
